import React from 'react';
import { Formik, Form, Field, ErrorMessage, FormikState } from 'formik';
import UICollapsibleSection from '@/components/shared/UICollapsibleSection';
import Input from '@/components/shared/Input';
import Button from '@/components/shared/Button';
import UICheckbox from '@/components/shared/UICheckbox';
import { SelectOption } from '@/types/api-types';
import './UnitInformation.scss';

export interface UnitInformationData {
  type: 'residential' | 'commercial' | 'industrial' | '' | null;
  floor: number | null;
  roomsCount: number | null;
  rooms: Array<{
    type?: string | null;
    floor?: number | null;
    flooring?: string | null;
    dimension?: number | null;
  }>;
  dimension: number | null;
  bedrooms: number | null;
  numberOfFloors: number | null;
  bathrooms: number | null;
  hasOtherInformation: boolean;
  otherInformation: string;
}

export interface UnitInformationOptions {
  roomTypes?: SelectOption<string>[];
  unitTypes?: SelectOption<string>[];
  floors?: SelectOption<number>[];
}

export interface UnitInformationProps {
  data: UnitInformationData;
  options?: UnitInformationOptions | null;
  onSave: (data: Partial<UnitInformationData>) => Promise<void>;
  loading?: boolean;
  collapsed?: boolean;
  onToggleCollapse?: (collapsed: boolean) => void;
  className?: string;
}

const baseClass = 'UnitInformation';

const defaultInitialValues: UnitInformationData = {
  type: null,
  floor: null,
  roomsCount: null,
  rooms: [{ type: null, floor: null, flooring: null, dimension: null }],
  dimension: null,
  bedrooms: null,
  numberOfFloors: null,
  bathrooms: null,
  hasOtherInformation: false,
  otherInformation: '',
};

const UnitInformation: React.FC<UnitInformationProps> = ({
  data,
  options,
  onSave,
  loading = false,
  collapsed,
  onToggleCollapse,
  className = '',
}) => {
  const handleSubmit = async (values: UnitInformationData) => {
    await onSave(values);
  };

  const handleClear = (
    resetForm: (nextState?: Partial<FormikState<UnitInformationData>>) => void,
  ) => {
    resetForm({ values: defaultInitialValues });
  };

  // Єдина зміна тут: якщо data.rooms порожній, ми створюємо один рядок-заготовку.
  // Вся інша логіка, включно з кнопками, залишається недоторканою.
  const initialValues = {
    ...data,
    rooms:
      data && data.rooms && data.rooms.length > 0
        ? data.rooms
        : [{ type: null, floor: null, flooring: null, dimension: null }],
  };

  return (
    <UICollapsibleSection
      title="Unit information"
      description=""
      collapsible={true}
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      loading={loading}
      loadingText="Loading unit information..."
      className={`${baseClass} ${className}`.trim()}
    >
      <Formik
        enableReinitialize={true}
        initialValues={initialValues} // Використовуємо підготовлені дані
        onSubmit={handleSubmit}
      >
        {({
          values,
          setFieldValue,
          errors,
          handleBlur,
          touched,
          handleChange,
          resetForm,
        }) => (
          <Form className={`${baseClass}__form`}>
            {/* Unit type */}
            <div className={`${baseClass}__type-section`}>
              <div className={`${baseClass}__field-label`}>Unit type</div>
              <Field as="select" name="type" className={`${baseClass}__select`}>
                <option value="">Select type</option>
                {options?.unitTypes?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Field>
              <ErrorMessage
                name="type"
                component="div"
                className={`${baseClass}__error`}
              />
            </div>

            {/* Main fields grid */}
            <div className={`${baseClass}__fields-grid`}>
              <div className={`${baseClass}__field-label`}>Floor</div>
              <Input
                className={`${baseClass}__field-input`}
                onChange={handleChange}
                type="number"
                onBlur={handleBlur}
                placeholder="Floor of the building"
                value={values.floor ?? ''}
                name="floor"
                errors={errors.floor && touched.floor ? errors.floor : ''}
              />

              <div className={`${baseClass}__field-label`}>Rooms</div>
              <Input
                className={`${baseClass}__field-input`}
                onChange={handleChange}
                type="number"
                onBlur={handleBlur}
                placeholder="Number of rooms"
                value={values.roomsCount ?? ''}
                name="roomsCount"
                errors={
                  errors.roomsCount && touched.roomsCount
                    ? errors.roomsCount
                    : ''
                }
              />

              <div className={`${baseClass}__field-label`}>Dimension</div>
              <Input
                className={`${baseClass}__field-input`}
                onChange={handleChange}
                type="number"
                onBlur={handleBlur}
                placeholder="Unit dimension"
                value={values.dimension ?? ''}
                name="dimension"
                errors={
                  errors.dimension && touched.dimension ? errors.dimension : ''
                }
              />

              <div className={`${baseClass}__field-label`}>Bedrooms</div>
              <Input
                className={`${baseClass}__field-input`}
                onChange={handleChange}
                type="number"
                onBlur={handleBlur}
                placeholder="Number of bedrooms"
                value={values.bedrooms ?? ''}
                name="bedrooms"
                errors={
                  errors.bedrooms && touched.bedrooms ? errors.bedrooms : ''
                }
              />

              <div className={`${baseClass}__field-label`}>
                Number of floors
              </div>
              <Input
                className={`${baseClass}__field-input`}
                onChange={handleChange}
                type="number"
                onBlur={handleBlur}
                placeholder="Number of floors"
                value={values.numberOfFloors ?? ''}
                name="numberOfFloors"
                errors={
                  errors.numberOfFloors && touched.numberOfFloors
                    ? errors.numberOfFloors
                    : ''
                }
              />

              <div className={`${baseClass}__field-label`}>Bathrooms</div>
              <Input
                className={`${baseClass}__field-input`}
                onChange={handleChange}
                type="number"
                onBlur={handleBlur}
                placeholder="Number of bathrooms"
                value={values.bathrooms ?? ''}
                name="bathrooms"
                errors={
                  errors.bathrooms && touched.bathrooms ? errors.bathrooms : ''
                }
              />
            </div>

            {/* Rooms list section */}
            <div className={`${baseClass}__rooms-section`}>
              {values.rooms.map((room, index) => {
                const isEmpty =
                  !room.type &&
                  !room.floor &&
                  !room.flooring &&
                  !room.dimension;
                return (
                  <div key={index} className={`${baseClass}__rooms-row`}>
                    <div className={`${baseClass}__rooms-label`}>Room</div>
                    <div className={`${baseClass}__rooms-fields`}>
                      <Field
                        as="select"
                        name={`rooms.${index}.type`}
                        className={`${baseClass}__rooms-select ${baseClass}__rooms-select--black-text ${
                          isEmpty ? `${baseClass}__rooms-select--empty` : ''
                        }`}
                      >
                        <option value="">Select type</option>
                        {options?.roomTypes?.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Field>
                      <Field
                        as="select"
                        name={`rooms.${index}.floor`}
                        className={`${baseClass}__rooms-select-floor ${
                          isEmpty ? `${baseClass}__rooms-select--empty` : ''
                        }`}
                        onChange={(
                          e: React.ChangeEvent<HTMLSelectElement>,
                        ) => {
                          const value = e.target.value;
                          setFieldValue(
                            `rooms.${index}.floor`,
                            value === '' ? null : Number(value),
                          );
                        }}
                      >
                        <option value="">Select floor</option>
                        {options?.floors?.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Field>
                      <Input
                        className={`${baseClass}__rooms-input ${
                          isEmpty ? `${baseClass}__rooms-input--empty` : ''
                        }`}
                        onChange={handleChange}
                        type="text"
                        onBlur={handleBlur}
                        placeholder="Flooring"
                        value={room.flooring || ''}
                        name={`rooms.${index}.flooring`}
                        errors=""
                      />
                      <Input
                        className={`${baseClass}__rooms-input ${
                          isEmpty ? `${baseClass}__rooms-input--empty` : ''
                        }`}
                        onChange={handleChange}
                        type="number"
                        onBlur={handleBlur}
                        placeholder="Dimension"
                        value={room.dimension ?? ''}
                        name={`rooms.${index}.dimension`}
                        errors=""
                      />
                    </div>
                    <div className={`${baseClass}__rooms-buttons`}>
                      {index === values.rooms.length - 1 ? (
                        <>
                          {values.rooms.length > 1 && (
                            <button
                              type="button"
                              className={`${baseClass}__rooms-button ${baseClass}__rooms-button--remove`}
                              onClick={() => {
                                const newRooms = values.rooms.filter(
                                  (_, i) => i !== index,
                                );
                                setFieldValue('rooms', newRooms);
                              }}
                            >
                              −
                            </button>
                          )}
                          <button
                            type="button"
                            className={`${baseClass}__rooms-button ${baseClass}__rooms-button--add`}
                            onClick={() => {
                              const newRooms = [
                                ...values.rooms,
                                {
                                  type: null,
                                  floor: null,
                                  flooring: null,
                                  dimension: null,
                                },
                              ];
                              setFieldValue('rooms', newRooms);
                            }}
                          >
                            +
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className={`${baseClass}__rooms-button ${baseClass}__rooms-button--remove`}
                          onClick={() => {
                            const newRooms = values.rooms.filter(
                              (_, i) => i !== index,
                            );
                            setFieldValue('rooms', newRooms);
                          }}
                        >
                          −
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Other information */}
            <div className={`${baseClass}__other-section`}>
              <label className={`${baseClass}__checkbox-label`}>
                <UICheckbox
                  name="hasOtherInformation"
                  checked={values.hasOtherInformation}
                  onChange={handleChange}
                />
                Other
              </label>
              <Field
                className={`${baseClass}__textarea`}
                as="textarea"
                placeholder="Specify"
                disabled={!values.hasOtherInformation}
                name="otherInformation"
                value={values.otherInformation}
                onChange={handleChange}
              />
            </div>

            {/* Action buttons */}
            <div className={`${baseClass}__actions`}>
              <Button type="submit" theme="primary">
                Save
              </Button>
              <Button
                type="button"
                theme="secondary"
                onClick={() => handleClear(resetForm)}
              >
                Clear
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </UICollapsibleSection>
  );
};

export default UnitInformation;
